import { Directive, inject, input } from '@angular/core'
import { Router } from '@angular/router'
import { Language } from '@pai/commons'

import {
  LinkTarget,
  LinkTarget_Type,
  LinkTargetOptional,
  LinkTargetOptional_Type,
  NavigationItem,
} from 'app/core/cms/generated/api-types'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { QueryParams } from 'app/static/query-params.enum'
import { RoutePath } from 'app/static/route-path.enum'
import { explicitComputed } from 'app/utils/signal-utils'

type LinkType = LinkTarget_Type | LinkTargetOptional_Type
type HandleableTarget = NavigationItem['target'] | LinkTarget | LinkTargetOptional

function createInternalUrl(language: Language, type: 'pages' | 'projects' | 'publications', slug: string): string[] {
  switch (type) {
    case 'pages':
      return [language, ...slug.split('/')]
    case 'projects':
      return [language, RoutePath.PROJECTS, slug]
    case 'publications':
      return [language, RoutePath.PUBLICATIONS, slug]
    default:
      return type
  }
}

@Directive({
  selector: 'a[paiCmsLink]',
  standalone: true,
  host: {
    '[href]': 'href()',
    '[attr.target]': 'linkType() === "relationship" ? null : "_blank"',
    '[attr.rel]': 'linkType() === "relationship" ? null : "noreferrer"',
    '[attr.download]': 'linkType() === "asset" ? "" : null',
    '(click)': 'onClick($event)',
  },
})
export class CmsLinkDirective {
  readonly target = input<HandleableTarget | null>(null, { alias: 'paiCmsLink' })
  readonly keepQueryParams = input<boolean>(false)

  readonly href = explicitComputed([this.target, this.keepQueryParams], this.createHref.bind(this))
  readonly linkType = explicitComputed(this.target, this.evaluateType.bind(this))

  private readonly router = inject(Router)
  private readonly lang = inject(CURRENT_LANG)

  protected onClick({ button, ctrlKey, metaKey, shiftKey }: MouseEvent): boolean {
    if (button !== 0 || ctrlKey || metaKey || shiftKey) {
      return true
    }
    if (this.linkType() !== 'relationship') {
      return true
    }
    const href = this.href()
    void (href && this.router.navigateByUrl(href))
    return false
  }

  private evaluateType(target: HandleableTarget | null): LinkType {
    if (!target) {
      return 'url'
    }
    if ('type' in target && target.type) {
      return target.type
    }
    return 'relationship'
  }

  private createHref(target: HandleableTarget | null, keepQueryParams: boolean): string | null {
    if (!target || ('enabled' in target && !target.enabled)) {
      return null
    }
    if (!('type' in target)) {
      return (
        this.router
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .createUrlTree(createInternalUrl(this.lang, target.relationTo!, target.value!.slug!), {
            queryParamsHandling: keepQueryParams ? 'merge' : null,
          })
          .toString()
      )
    }

    if (target.type === 'relationship' && target.internal?.value) {
      // internal can have filter (categories):
      const queryParams = target.filter?.value ? { [QueryParams.FILTER_ITEMS]: target.filter.value?.slug ?? '' } : {}

      return this.router
        .createUrlTree(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          createInternalUrl(this.lang, target.internal.relationTo!, target.internal.value.slug!),
          {
            queryParamsHandling: keepQueryParams ? 'merge' : null,
            queryParams,
          },
        )
        .toString()
    } else if (target.type === 'url' && target.url) {
      return /^https?:\/\//.test(target.url) ? target.url : `https://${target.url}`
    } else if (target.type === 'email' && target.email) {
      return `mailto:${target.email}`
    } else if (target.type === 'asset' && target.asset?.url) {
      return target.asset.url
    }

    return null
  }
}
